import { canonicalJson } from '@hoedokloop/backup-crypto';
import { isPortableSetting } from '@hoedokloop/domain';
import type { StudyFixture } from '@hoedokloop/domain/fixtures';
import type {
  Exam,
  MockScore,
  NoteInbox,
  PortableSetting,
  Instant,
  Milestone,
  MogakSession,
  PhotoAsset,
  Plan,
  PlanItem,
  StudyLog,
  StudyStoreMap,
  StudyStoreName,
  Subject,
  UUID,
  WrongNote,
} from '@hoedokloop/domain';
import Dexie, { type Table } from 'dexie';

import type { ImportCheckpoint } from './database';
import type { ParsedExportArchive } from './archive';
import {
  materializeStudyStoreValue,
  persistStudyStoreValue,
  type IndexedDbStudyRepository,
} from './repository';

export type ImportMode = 'merge' | 'replace';
export type ImportDecision = 'insert' | 'update' | 'delete' | 'ignored' | 'conflict';

export type ImportPreview = {
  mode: ImportMode;
  totals: Record<ImportDecision, number>;
  retainedNewer: number;
  byStore: Record<StudyStoreName, Record<ImportDecision, number>>;
  conflicts: Array<{ store: StudyStoreName; id: UUID; reason: string }>;
};

export type CheckpointSealer = {
  seal(snapshot: StudyFixture): Promise<Blob>;
};

export type ImportApplyResult =
  | { ok: true; preview: ImportPreview; checkpointId: UUID }
  | {
      ok: false;
      stage: 'preview' | 'checkpoint' | 'apply' | 'verify';
      preview: ImportPreview;
      checkpointId?: UUID;
    };

const stores: StudyStoreName[] = [
  'exams',
  'subjects',
  'studyLogs',
  'wrongNotes',
  'photoAssets',
  'plans',
  'planItems',
  'milestones',
  'mogakSessions',
  'mockScores',
  'noteInbox',
];

function emptyCounts(): Record<ImportDecision, number> {
  return { insert: 0, update: 0, delete: 0, ignored: 0, conflict: 0 };
}

function serialized(value: unknown): string {
  const json = JSON.stringify(value, (_key, nested) =>
    nested instanceof Blob ? '[Blob]' : nested,
  );
  return json === undefined ? 'undefined' : canonicalJson(JSON.parse(json));
}

function updatedAt(value: StudyStoreMap[StudyStoreName]): string | undefined {
  if ('updatedAt' in value) return value.updatedAt;
  if ('generatedAt' in value) return value.generatedAt;
  if ('earnedAt' in value) return value.earnedAt;
  if ('joinedAt' in value) return value.leftAt ?? value.joinedAt;
  if ('createdAt' in value) return value.deletedAt ?? value.createdAt;
  return undefined;
}

function isDeleted(value: StudyStoreMap[StudyStoreName]): boolean {
  return 'deletedAt' in value && value.deletedAt !== undefined;
}

function mergeDecision(
  current: StudyStoreMap[StudyStoreName] | undefined,
  incoming: StudyStoreMap[StudyStoreName],
): { decision: ImportDecision; reason?: string } {
  if (!current) return { decision: isDeleted(incoming) ? 'ignored' : 'insert' };
  if (serialized(current) === serialized(incoming)) return { decision: 'ignored' };
  const currentTime = updatedAt(current);
  const incomingTime = updatedAt(incoming);
  if (currentTime && incomingTime) {
    if (incomingTime > currentTime) return { decision: isDeleted(incoming) ? 'delete' : 'update' };
    if (incomingTime < currentTime) return { decision: 'ignored', reason: 'local-newer' };
  }
  return { decision: 'conflict', reason: 'same-version content differs' };
}

export function materializeArchiveData(archive: ParsedExportArchive): StudyFixture {
  const photoAssets = archive.data.photoAssets.map<PhotoAsset>((photo) => {
    const original = archive.photoFiles[photo.originalPath];
    const thumbnail = archive.photoFiles[photo.thumbnailPath];
    if (!original || !thumbnail) throw new Error('Photo bytes are missing');
    return {
      id: photo.id as UUID,
      original: new Blob([original as BlobPart], { type: photo.mimeType }),
      thumbnail: new Blob([thumbnail as BlobPart], { type: photo.mimeType }),
      mimeType: photo.mimeType,
      width: photo.width,
      height: photo.height,
      byteLength: photo.byteLength,
      sha256: photo.sha256,
      createdAt: photo.createdAt as Instant,
      ...(photo.deletedAt === undefined ? {} : { deletedAt: photo.deletedAt as Instant }),
    };
  });
  return {
    mockScores: (archive.data.mockScores ?? []) as MockScore[],
    noteInbox: (archive.data.noteInbox ?? []) as NoteInbox[],
    userSettings: (archive.data.userSettings ?? []) as PortableSetting[],
    exams: archive.data.exams as Exam[],
    subjects: archive.data.subjects as Subject[],
    studyLogs: archive.data.studyLogs as StudyLog[],
    wrongNotes: archive.data.wrongNotes as WrongNote[],
    photoAssets,
    plans: archive.data.plans as Plan[],
    planItems: archive.data.planItems as PlanItem[],
    milestones: archive.data.milestones as Milestone[],
    mogakSessions: archive.data.mogakSessions as MogakSession[],
  };
}

function fixtureStore<Store extends StudyStoreName>(
  fixture: StudyFixture,
  store: Store,
): StudyStoreMap[Store][] {
  return (fixture[store] ?? []) as StudyStoreMap[Store][];
}

export class ImportService {
  readonly repository: IndexedDbStudyRepository;
  readonly sealer: CheckpointSealer;
  readonly now: () => Instant;
  readonly createId: () => UUID;

  constructor(options: {
    repository: IndexedDbStudyRepository;
    sealer: CheckpointSealer;
    now: () => Instant;
    createId: () => UUID;
  }) {
    this.repository = options.repository;
    this.sealer = options.sealer;
    this.now = options.now;
    this.createId = options.createId;
  }

  private table<Store extends StudyStoreName>(store: Store): Table<StudyStoreMap[Store], UUID> {
    return this.repository.database.table(store) as Table<StudyStoreMap[Store], UUID>;
  }

  private tables(): Table[] {
    return [...stores.map((store) => this.table(store)), this.repository.database.settings];
  }

  private async snapshot(): Promise<StudyFixture> {
    const entries = await Promise.all(
      stores.map(async (store) =>
        (await this.table(store).toArray()).map((value) =>
          materializeStudyStoreValue(store, value),
        ),
      ),
    );
    return {
      ...Object.fromEntries(stores.map((store, index) => [store, entries[index] ?? []])),
      userSettings: (await this.repository.database.settings.toArray()).filter((item) =>
        isPortableSetting(item.key),
      ),
    } as StudyFixture;
  }

  async preview(incoming: StudyFixture, mode: ImportMode): Promise<ImportPreview> {
    const byStore = Object.fromEntries(stores.map((store) => [store, emptyCounts()])) as Record<
      StudyStoreName,
      Record<ImportDecision, number>
    >;
    const conflicts: ImportPreview['conflicts'] = [];
    let retainedNewer = 0;
    for (const store of stores) {
      const current = new Map(
        (await this.table(store).toArray()).map((value) => {
          const materialized = materializeStudyStoreValue(store, value);
          return [materialized.id, materialized] as const;
        }),
      );
      const incomingValues = fixtureStore(incoming, store);
      const incomingIds = new Set(incomingValues.map((value) => value.id));
      for (const value of incomingValues) {
        const compared =
          mode === 'replace'
            ? {
                decision: current.has(value.id)
                  ? isDeleted(value)
                    ? ('delete' as const)
                    : ('update' as const)
                  : isDeleted(value)
                    ? ('ignored' as const)
                    : ('insert' as const),
              }
            : mergeDecision(current.get(value.id), value);
        byStore[store][compared.decision] += 1;
        if ('reason' in compared && compared.reason === 'local-newer') retainedNewer += 1;
        if (compared.decision === 'conflict') {
          conflicts.push({
            store,
            id: value.id,
            reason: compared.reason ?? 'content conflict',
          });
        }
      }
      if (mode === 'replace') {
        byStore[store].delete += [...current.keys()].filter((id) => !incomingIds.has(id)).length;
      }
    }
    const totals = emptyCounts();
    for (const counts of Object.values(byStore)) {
      for (const decision of Object.keys(totals) as ImportDecision[]) {
        totals[decision] += counts[decision];
      }
    }
    return { mode, totals, retainedNewer, byStore, conflicts };
  }

  protected async afterWriteBeforeVerify(): Promise<void> {}

  private async verifyReferences(): Promise<void> {
    const exams = new Set((await this.repository.database.exams.toArray()).map((item) => item.id));
    const subjects = new Map(
      (await this.repository.database.subjects.toArray()).map((item) => [item.id, item]),
    );
    const photos = new Set(
      (await this.repository.database.photoAssets.toArray()).map((item) => item.id),
    );
    const plans = new Map(
      (await this.repository.database.plans.toArray()).map((item) => [item.id, item]),
    );
    for (const subject of subjects.values()) {
      if (!exams.has(subject.examId)) throw new Error('Persisted subject reference failed');
    }
    for (const log of await this.repository.database.studyLogs.toArray()) {
      if (subjects.get(log.subjectId)?.examId !== log.examId)
        throw new Error('Persisted log reference failed');
    }
    for (const note of await this.repository.database.wrongNotes.toArray()) {
      if (subjects.get(note.subjectId)?.examId !== note.examId)
        throw new Error('Persisted note reference failed');
      if (note.photoAssetId && !photos.has(note.photoAssetId))
        throw new Error('Persisted photo reference failed');
    }
    for (const score of await this.repository.database.mockScores.toArray()) {
      if (
        !exams.has(score.examId) ||
        score.scores.some((item) => subjects.get(item.subjectId)?.examId !== score.examId)
      )
        throw new Error('Persisted score reference failed');
    }
    for (const item of await this.repository.database.noteInbox.toArray()) {
      if (!photos.has(item.photoAssetId)) throw new Error('Persisted inbox reference failed');
    }
    for (const item of await this.repository.database.planItems.toArray()) {
      const plan = plans.get(item.planId);
      if (!plan || subjects.get(item.subjectId)?.examId !== plan.examId) {
        throw new Error('Persisted plan item reference failed');
      }
    }
  }

  async apply(incoming: StudyFixture, mode: ImportMode): Promise<ImportApplyResult> {
    const preview = await this.preview(incoming, mode);
    if (preview.conflicts.length > 0) return { ok: false, stage: 'preview', preview };
    const checkpointId = this.createId();
    let sealed: Blob;
    try {
      sealed = await this.sealer.seal(await this.snapshot());
      const checkpoint: ImportCheckpoint = {
        id: checkpointId,
        encryptedPayload: new Uint8Array(await sealed.arrayBuffer()),
        status: 'created',
        createdAt: this.now(),
      };
      await this.repository.database.importCheckpoints.put(checkpoint);
    } catch {
      return { ok: false, stage: 'checkpoint', preview };
    }

    let stage: 'apply' | 'verify' = 'apply';
    try {
      await this.repository.database.transaction('rw', this.tables(), async () => {
        for (const store of stores) {
          const table = this.table(store);
          const values = fixtureStore(incoming, store);
          const persistedValues = await Dexie.waitFor(
            Promise.all(values.map((value) => persistStudyStoreValue(store, value))),
          );
          if (mode === 'replace') {
            await table.clear();
            if (persistedValues.length > 0) await table.bulkPut(persistedValues);
            continue;
          }
          const current = new Map(
            (await table.toArray()).map((value) => {
              const materialized = materializeStudyStoreValue(store, value);
              return [materialized.id, materialized] as const;
            }),
          );
          for (const [index, value] of values.entries()) {
            const decision = mergeDecision(current.get(value.id), value).decision;
            if (decision === 'insert' || decision === 'update' || decision === 'delete') {
              const persistedValue = persistedValues[index];
              if (!persistedValue) throw new Error('Persisted import value is missing');
              await table.put(persistedValue);
            }
          }
        }
        if (mode === 'replace') {
          const old = await this.repository.database.settings.toArray();
          await this.repository.database.settings.bulkDelete(
            old.filter((item) => isPortableSetting(item.key)).map((item) => item.key),
          );
        }
        for (const setting of incoming.userSettings ?? []) {
          if (!isPortableSetting(setting.key)) throw new Error('Non-portable setting');
          const current = await this.repository.database.settings.get(setting.key);
          if (mode === 'replace' || !current || current.updatedAt < setting.updatedAt)
            await this.repository.database.settings.put(setting);
        }
        await Dexie.waitFor(this.afterWriteBeforeVerify());
        stage = 'verify';
        await this.verifyReferences();
      });
    } catch {
      await this.repository.database.importCheckpoints.update(checkpointId, { status: 'failed' });
      return { ok: false, stage, preview, checkpointId };
    }
    await this.repository.database.importCheckpoints.update(checkpointId, { status: 'restored' });
    return { ok: true, preview, checkpointId };
  }
}
